import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useAuth } from "./AuthContext";
import { useAgent } from "./AgentContext";
import { usePermissions } from "../hooks/usePermissions";
import { scenariosAPI, callObservationsAPI, metricsAPI, aiTestingAgentAPI } from "../services/api";
import { ONBOARDING_STEPS } from "../components/onboarding/steps";
import { lsRead, lsWrite, lsReadArr, lsWriteArr } from "../utils/wsPrefs";

const OnboardingContext = createContext(null);

// Onboarding auto-generation (backend Onboarding::AutoGeneration) reports its
// progress on agent.auto_generation: { scenarios: {status, scenario_ids},
// metrics: {status, metric_ids} }. While a side is in progress we poll the agent.
const AUTO_GEN_POLL_MS = 5000;
const AUTO_GEN_IN_PROGRESS = ["pending", "running"];
const AUTO_GEN_IDS_FIELD = { scenarios: "scenario_ids", metrics: "metric_ids" };
// Generation takes a minute or two. Still "in progress" this long after the
// agent was created means the job never ran (e.g. workers down) — show the
// failed state (manual buttons) instead of a spinner forever.
const AUTO_GEN_STALE_MS = 15 * 60 * 1000;

export const OnboardingProvider = ({ children }) => {
  const { currentWorkspace } = useAuth();
  const { agents, agentsLoading } = useAgent();
  const { canWrite, isViewer } = usePermissions();
  const wsId = currentWorkspace?.id;

  const [counts, setCounts] = useState({
    scenarios: 0,
    batches: 0,
    conversations: 0,
    customMetrics: 0,
  });
  const [countsLoading, setCountsLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);
  const [completedFlag, setCompletedFlag] = useState(false);
  // `celebrationSeen` is a per-mount snapshot: it only reflects whether the
  // celebration was ALREADY seen before this session, so a refresh/re-login
  // (fresh mount reading the now-persisted flag) makes the card disappear.
  const [celebrationSeen, setCelebrationSeen] = useState(false);
  // In-session hide when the user clicks "Go to dashboard".
  const [celebrationDismissed, setCelebrationDismissed] = useState(false);
  const [skipped, setSkipped] = useState([]);

  // Read persisted per-workspace flags whenever the workspace changes.
  useEffect(() => {
    if (!wsId) return;
    setDismissed(lsRead("onboarding-dismissed", wsId));
    setCompletedFlag(lsRead("onboarding-completed", wsId));
    setCelebrationSeen(lsRead("onboarding-celebration-seen", wsId));
    setCelebrationDismissed(false);
    setSkipped(lsReadArr("onboarding-skipped-steps", wsId));
  }, [wsId]);

  // Fetch the server-side counts. Refetchable so the checklist can check off
  // steps mid-session (step 1/agents already updates live via AgentContext).
  // `latestWsRef` drops results from a workspace we've since left.
  // `silent` refetches in the background without flipping `loading` (which
  // would swap the checklist for a spinner mid-session).
  const latestWsRef = useRef(null);
  const refresh = useCallback(({ silent = false } = {}) => {
    if (!wsId) return;
    const forWs = wsId;
    latestWsRef.current = forWs;
    if (!silent) setCountsLoading(true);
    Promise.allSettled([
      scenariosAPI.getBatchesWithPagination(1, forWs),
      scenariosAPI.getScenarios(forWs),
      callObservationsAPI.getCallObservations(forWs, { per_page: 1 }),
      metricsAPI.getAllMetrics(forWs, { per_page: 1 }),
    ]).then(([batchesRes, scenariosRes, convoRes, metricsRes]) => {
      if (latestWsRef.current !== forWs) return; // workspace changed mid-flight
      const next = { scenarios: 0, batches: 0, conversations: 0, customMetrics: 0 };
      if (batchesRes.status === "fulfilled") {
        next.batches = batchesRes.value?.data?.counts?.total || 0;
      }
      if (scenariosRes.status === "fulfilled") {
        const data = scenariosRes.value?.data;
        next.scenarios = Array.isArray(data) ? data.length : data?.data?.length || 0;
      }
      if (convoRes.status === "fulfilled") {
        next.conversations = convoRes.value?.data?.meta?.total_count || 0;
      }
      if (metricsRes.status === "fulfilled") {
        // getAllMetrics returns { standard_metrics, data (custom), global_metrics }.
        // Only custom/generated metrics count — predefined ones always exist.
        const custom = metricsRes.value?.data;
        next.customMetrics = Array.isArray(custom) ? custom.length : 0;
      }
      setCounts(next);
      setCountsLoading(false);
    });
  }, [wsId]);

  // Fetch on mount / workspace change, and again when the tab regains focus
  // (covers returning from a vendor tab after connecting calls).
  useEffect(() => {
    refresh();
    const onFocus = () => refresh();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [refresh]);

  // Auto-generation state, seeded from the agent that carries it and kept fresh
  // by polling while either side is still generating.
  const autoGenAgent = agents.find(
    (a) => a?.auto_generation?.scenarios || a?.auto_generation?.metrics,
  );
  const autoGenAgentId = autoGenAgent?.id;
  const agentAutoGeneration = autoGenAgent?.auto_generation;
  const [autoGeneration, setAutoGeneration] = useState(null);
  useEffect(() => {
    setAutoGeneration(agentAutoGeneration || null);
  }, [agentAutoGeneration]);

  const autoGenCreatedAt = autoGenAgent?.created_at ? new Date(autoGenAgent.created_at).getTime() : null;
  const autoGenStale = !!autoGenCreatedAt && Date.now() - autoGenCreatedAt > AUTO_GEN_STALE_MS;
  const autoGenStatus = (key) => {
    const status = autoGeneration?.[key]?.status;
    return autoGenStale && AUTO_GEN_IN_PROGRESS.includes(status) ? "failed" : status;
  };

  const autoGenerating = Object.keys(AUTO_GEN_IDS_FIELD).some((k) =>
    AUTO_GEN_IN_PROGRESS.includes(autoGenStatus(k)),
  );
  useEffect(() => {
    if (!autoGenerating || !autoGenAgentId || !wsId) return;
    let cancelled = false;
    const timer = setInterval(async () => {
      try {
        const fresh = await aiTestingAgentAPI.getAgent(autoGenAgentId, wsId);
        if (!cancelled && fresh?.auto_generation) setAutoGeneration(fresh.auto_generation);
      } catch (_) {
        /* transient — try again on the next tick */
      }
    }, AUTO_GEN_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [autoGenerating, autoGenAgentId, wsId]);

  // When a side finishes, refetch counts so its step checks off.
  const prevAutoGenerationRef = useRef(null);
  useEffect(() => {
    const prev = prevAutoGenerationRef.current;
    prevAutoGenerationRef.current = autoGeneration;
    if (!prev || !autoGeneration) return;
    const justCompleted = Object.keys(AUTO_GEN_IDS_FIELD).some(
      (k) => autoGeneration[k]?.status === "completed" && prev[k]?.status !== "completed",
    );
    if (justCompleted) refresh({ silent: true });
  }, [autoGeneration, refresh]);

  const autoGenerationFor = (key) => {
    const side = key && autoGeneration?.[key];
    if (!side?.status) return null;
    return {
      status: autoGenStatus(key),
      ids: side[AUTO_GEN_IDS_FIELD[key]] || [],
      agentName: autoGenAgent?.name,
    };
  };

  // A step is done when its real-data signal fires OR the user chose to skip it
  // (skipping assumes the step complete). `skipped` is a persisted id list.
  const doneById = {
    "create-agent": agents.length > 0,
    "add-scenarios": counts.scenarios > 0,
    "run-batch": counts.batches > 0,
    "define-metrics": counts.customMetrics > 0,
    "connect-calls": counts.conversations > 0,
  };
  const steps = ONBOARDING_STEPS.map((s) => ({
    ...s,
    done: !!doneById[s.id] || skipped.includes(s.id),
    skipped: skipped.includes(s.id) && !doneById[s.id],
    autoGeneration: autoGenerationFor(s.autoGenerate?.key),
  }));
  const total = steps.length;
  const completed = steps.filter((s) => s.done).length;
  const percent = total ? Math.round((completed / total) * 100) : 0;
  const dataComplete = completed === total;

  // Completion is terminal: once observed, persist and never resurface.
  useEffect(() => {
    if (dataComplete && wsId && !completedFlag) {
      lsWrite("onboarding-completed", wsId, true);
      setCompletedFlag(true);
    }
  }, [dataComplete, wsId, completedFlag]);

  const isComplete = dataComplete || completedFlag;
  const loading = agentsLoading || countsLoading;

  // Persist "celebration seen" as soon as the card is eligible to show — not on
  // button click — so leaving/refreshing/re-logging-in makes it gone. The card
  // still shows THIS session because `celebrationSeen` is a mount snapshot.
  useEffect(() => {
    if (canWrite && isComplete && !celebrationSeen && wsId) {
      lsWrite("onboarding-celebration-seen", wsId, true);
    }
  }, [canWrite, isComplete, celebrationSeen, wsId]);

  const dismiss = () => {
    if (wsId) lsWrite("onboarding-dismissed", wsId, true);
    setDismissed(true);
  };
  const reopen = () => {
    if (wsId) lsWrite("onboarding-dismissed", wsId, false);
    setDismissed(false);
  };
  const ackCompletion = () => {
    // Persisted by the effect above; this just hides it for the current session.
    setCelebrationDismissed(true);
  };
  const skipStep = (id) => {
    setSkipped((prev) => {
      if (prev.includes(id)) return prev;
      const next = [...prev, id];
      if (wsId) lsWriteArr("onboarding-skipped-steps", wsId, next);
      return next;
    });
  };

  const showOnboarding = !!canWrite && !loading && !dismissed && !isComplete;
  const showCompletionCelebration =
    !!canWrite && isComplete && !celebrationSeen && !celebrationDismissed;

  const value = {
    steps,
    completed,
    total,
    percent,
    isComplete,
    dismissed,
    dismiss,
    reopen,
    ackCompletion,
    skipStep,
    showOnboarding,
    showCompletionCelebration,
    canWrite: !!canWrite,
    isViewer: !!isViewer,
    loading,
    refresh,
  };

  return <OnboardingContext.Provider value={value}>{children}</OnboardingContext.Provider>;
};

export const useOnboarding = () => {
  const ctx = useContext(OnboardingContext);
  if (!ctx) throw new Error("useOnboarding must be used within OnboardingProvider");
  return ctx;
};
